import { Component, type ErrorInfo, type ReactNode } from "react";
import * as Sentry from "@sentry/react";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ErrorPage } from "@/components/waggle/ErrorPage";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
  componentStack: string | null;
}

// App-wide error boundary. A render-time exception anywhere below this used to
// unmount the whole React tree and leave a blank white page (and client-side
// navigation like the back button wouldn't recover it — only a full reload).
// This catches the error and keeps the app shell alive with a recoverable page.
// The message and stack are shown only in dev; production users get a plain
// explanation and the error goes to Sentry.
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null, componentStack: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[ErrorBoundary] Caught render error:", error, info.componentStack);
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
    this.setState({ componentStack: info.componentStack ?? null });
  }

  handleReset = () => {
    this.setState({ error: null, componentStack: null });
  };

  // Hard navigation on purpose: it also clears whatever state made the render fail.
  handleHome = () => {
    window.location.assign(localStorage.getItem("auth_token") ? "/dashboard" : "/");
  };

  render() {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    return (
      <ErrorPage
        data-testid="error-page"
        tone="warning"
        icon={<AlertTriangle />}
        title="Something went wrong"
        description="We hit an unexpected problem loading this page. Your saved work is safe."
        actions={
          <>
            <Button variant="outline" onClick={this.handleReset} data-testid="button-try-again">
              Try again
            </Button>
            <Button onClick={this.handleHome} data-testid="button-go-home">
              Go home
            </Button>
          </>
        }
      >
        {import.meta.env.DEV && (
          <pre
            data-testid="error-details"
            className="max-h-48 overflow-auto whitespace-pre-wrap rounded-md bg-muted p-3 text-left text-xs text-foreground"
          >
            {error.message}
            {componentStack}
          </pre>
        )}
      </ErrorPage>
    );
  }
}
